import { useCallback, useEffect, useId, useState } from 'react';

import { ApiError } from '../../api/client';
import {
  sipApi,
  type SipConfigView,
  type SipConfigWriteInput,
  type SipTestResult,
} from '../../api/sip';
import {
  Alert,
  Button,
  Card,
  FormField,
  Input,
  LinkButton,
  LoadingState,
  PasswordInput,
  Section,
  Select,
  Toggle,
} from '../../components';
import { useI18n, type TranslationKey } from '../../i18n';
import { useSession } from '../../auth/session-context';

/**
 * Zugangsdaten des Telefonieanbieters einer Kampagne
 * (06_telephony_sip_inbound.md).
 *
 * Die Maske ist bewusst kurz: Anbietername, Server, Benutzername, Passwort
 * und ein Haken für die Registrierung. Alles Technische liegt hinter
 * „Erweiterte Einstellungen" und bleibt zugeklappt, solange niemand es
 * braucht.
 *
 * Es gibt **keinen** anbieterspezifischen Code. Anbieter wie TENIOS, dus.net
 * oder sipgate unterscheiden sich nur in den Werten, die hier eingetragen
 * werden; der Anbietername ist eine reine Notiz.
 */
export function SipConfigSection({ campaignId }: { campaignId: string }): JSX.Element {
  const { t, tError } = useI18n();
  const { handleApiError } = useSession();

  const anbieterId = useId();
  const serverId = useId();
  const benutzerId = useId();
  const authId = useId();
  const realmId = useId();
  const portId = useId();
  const transportId = useId();
  const fromDomainId = useId();
  const ablaufId = useId();
  const wiederholungId = useId();

  const [geladen, setGeladen] = useState(false);
  const [vorhanden, setVorhanden] = useState<SipConfigView | null>(null);
  const [formular, setFormular] = useState<SipConfigWriteInput>(leeresFormular());
  const [erweitert, setErweitert] = useState(false);

  const [fehler, setFehler] = useState<string | null>(null);
  const [meldung, setMeldung] = useState<string | null>(null);
  const [testErgebnis, setTestErgebnis] = useState<SipTestResult | null>(null);
  const [busy, setBusy] = useState(false);
  const [testet, setTestet] = useState(false);

  const laden = useCallback(async () => {
    try {
      const antwort = await sipApi.get(campaignId);
      setVorhanden(antwort.global);
      if (antwort.global) {
        setFormular(ausView(antwort.global));
        // Hat jemand erweiterte Werte gesetzt, wird der Bereich geöffnet -
        // sonst wären sie unsichtbar.
        setErweitert(hatErweiterteWerte(antwort.global));
      }
    } catch (caught) {
      handleApiError(caught);
      setFehler(caught instanceof ApiError ? tError(caught.code) : tError(undefined));
    } finally {
      setGeladen(true);
    }
  }, [campaignId, handleApiError, tError]);

  useEffect(() => {
    void laden();
  }, [laden]);

  const aendern = (teil: Partial<SipConfigWriteInput>): void => {
    setFormular((alt) => ({ ...alt, ...teil }));
    setMeldung(null);
    setTestErgebnis(null);
  };

  const speichern = (): void => {
    void (async () => {
      setFehler(null);
      setMeldung(null);
      setTestErgebnis(null);
      setBusy(true);
      try {
        await sipApi.saveGlobal(campaignId, formular);
        setMeldung(t('campaign.sip.saved'));
        await laden();
      } catch (caught) {
        handleApiError(caught);
        setFehler(caught instanceof ApiError ? tError(caught.code) : tError(undefined));
      } finally {
        setBusy(false);
      }
    })();
  };

  const testen = (): void => {
    void (async () => {
      setFehler(null);
      setMeldung(null);
      setTestErgebnis(null);
      setTestet(true);
      try {
        const antwort = await sipApi.test(campaignId);
        setTestErgebnis(antwort.result);
      } catch (caught) {
        handleApiError(caught);
        setFehler(caught instanceof ApiError ? tError(caught.code) : tError(undefined));
      } finally {
        setTestet(false);
      }
    })();
  };

  if (!geladen) {
    return (
      <Card>
        <Section title={t('campaign.section.telephony')}>
          <LoadingState />
        </Section>
      </Card>
    );
  }

  const vollstaendig =
    formular.host.trim() !== '' &&
    formular.username.trim() !== '' &&
    (Boolean(formular.password) || Boolean(vorhanden?.hasPassword));

  return (
    <Card>
      <Section
        title={t('campaign.section.telephony')}
        description={t('campaign.section.telephonyHint')}
      >
        {fehler ? <Alert tone="error">{fehler}</Alert> : null}
        {meldung ? <Alert tone="success">{meldung}</Alert> : null}
        {testErgebnis ? (
          <Alert tone={testErgebnis === 'ok' || testErgebnis === 'registered' ? 'success' : 'error'}>
            {t(('campaign.sip.test.' + testErgebnis) as TranslationKey)}
          </Alert>
        ) : null}

        <FormField label={t('campaign.sip.provider')} hint={t('campaign.sip.providerHint')} htmlFor={anbieterId}>
          <Input
            id={anbieterId}
            value={formular.providerName ?? ''}
            onChange={(wert) => aendern({ providerName: wert })}
          />
        </FormField>

        <FormField label={t('campaign.sip.host')} hint={t('campaign.sip.hostHint')} htmlFor={serverId}>
          <Input id={serverId} value={formular.host} onChange={(wert) => aendern({ host: wert })} />
        </FormField>

        <FormField label={t('campaign.sip.username')} htmlFor={benutzerId}>
          <Input
            id={benutzerId}
            value={formular.username}
            onChange={(wert) => aendern({ username: wert })}
          />
        </FormField>

        <FormField
          label={t('campaign.sip.password')}
          hint={vorhanden?.hasPassword ? t('campaign.sip.passwordStored') : undefined}
        >
          {/* Ein gespeichertes Passwort wird nie angezeigt - das Feld bleibt
              leer, und leer bedeutet "unverändert lassen". */}
          <PasswordInput
            value={formular.password ?? ''}
            onChange={(wert) => aendern({ password: wert })}
            autoComplete="new-password"
          />
        </FormField>

        <Toggle
          checked={formular.registrationEnabled ?? false}
          onChange={(wert) => aendern({ registrationEnabled: wert })}
          label={t('campaign.sip.registration')}
          help={t('campaign.sip.registrationHelp')}
        />

        <p>
          <LinkButton onClick={() => setErweitert(!erweitert)}>
            {erweitert ? t('campaign.sip.advancedHide') : t('campaign.sip.advancedShow')}
          </LinkButton>
        </p>

        {erweitert ? (
          <div className="form">
            <FormField label={t('campaign.sip.authId')} hint={t('campaign.sip.authIdHint')} htmlFor={authId}>
              <Input
                id={authId}
                value={formular.authId ?? ''}
                onChange={(wert) => aendern({ authId: wert })}
              />
            </FormField>

            <FormField label={t('campaign.sip.realm')} hint={t('campaign.sip.optional')} htmlFor={realmId}>
              <Input
                id={realmId}
                value={formular.realm ?? ''}
                onChange={(wert) => aendern({ realm: wert })}
              />
            </FormField>

            <FormField label={t('campaign.sip.fromDomain')} hint={t('campaign.sip.fromDomainHint')} htmlFor={fromDomainId}>
              <Input
                id={fromDomainId}
                value={formular.fromDomain ?? ''}
                onChange={(wert) => aendern({ fromDomain: wert })}
              />
            </FormField>

            <FormField label={t('campaign.sip.port')} hint={t('campaign.sip.portHint')} htmlFor={portId}>
              <Input
                id={portId}
                type="number"
                value={formular.port === null || formular.port === undefined ? '' : String(formular.port)}
                onChange={(wert) => aendern({ port: wert.trim() === '' ? null : Number(wert) })}
              />
            </FormField>

            <FormField label={t('campaign.sip.transport')} htmlFor={transportId}>
              <Select
                id={transportId}
                value={formular.transport ?? ''}
                options={[
                  { value: '', label: t('campaign.sip.transportDefault') },
                  { value: 'udp', label: 'UDP' },
                  { value: 'tcp', label: 'TCP' },
                  { value: 'tls', label: 'TLS' },
                ]}
                onChange={(wert) =>
                  aendern({ transport: wert === '' ? null : (wert as 'udp' | 'tcp' | 'tls') })
                }
              />
            </FormField>

            {/* Nur sinnvoll, wenn überhaupt registriert wird. */}
            {formular.registrationEnabled ? (
              <>
                <FormField
                  label={t('campaign.sip.expiration')}
                  hint={t('campaign.sip.expirationHint')}
                  htmlFor={ablaufId}
                >
                  <Input
                    id={ablaufId}
                    type="number"
                    value={
                      formular.registrationExpiration === null ||
                      formular.registrationExpiration === undefined
                        ? ''
                        : String(formular.registrationExpiration)
                    }
                    onChange={(wert) =>
                      aendern({
                        registrationExpiration: wert.trim() === '' ? null : Number(wert),
                      })
                    }
                  />
                </FormField>

                <FormField
                  label={t('campaign.sip.retryInterval')}
                  hint={t('campaign.sip.retryIntervalHint')}
                  htmlFor={wiederholungId}
                >
                  <Input
                    id={wiederholungId}
                    type="number"
                    value={
                      formular.registrationRetryInterval === null ||
                      formular.registrationRetryInterval === undefined
                        ? ''
                        : String(formular.registrationRetryInterval)
                    }
                    onChange={(wert) =>
                      aendern({
                        registrationRetryInterval: wert.trim() === '' ? null : Number(wert),
                      })
                    }
                  />
                </FormField>
              </>
            ) : null}
          </div>
        ) : null}

        <div className="row-actions">
          <Button variant="primary" disabled={!vollstaendig || busy} onClick={speichern}>
            {t('action.save')}
          </Button>
          <Button disabled={!vorhanden || testet} onClick={testen}>
            {testet ? t('campaign.sip.testing') : t('campaign.sip.test')}
          </Button>
        </div>
      </Section>
    </Card>
  );
}

function leeresFormular(): SipConfigWriteInput {
  return {
    host: '',
    username: '',
    password: '',
    providerName: '',
    registrationEnabled: false,
    authId: '',
    realm: '',
    fromDomain: '',
    port: null,
    transport: null,
    registrationExpiration: null,
    registrationRetryInterval: null,
  };
}

function ausView(view: SipConfigView): SipConfigWriteInput {
  return {
    host: view.host,
    username: view.username,
    // Ein gespeichertes Passwort wird nicht ausgeliefert und bleibt leer.
    password: '',
    providerName: view.providerName ?? '',
    registrationEnabled: view.registrationEnabled,
    authId: view.authId ?? '',
    realm: view.realm ?? '',
    fromDomain: view.fromDomain ?? '',
    port: view.port,
    transport: view.transport,
    registrationExpiration: view.registrationExpiration,
    registrationRetryInterval: view.registrationRetryInterval,
  };
}

function hatErweiterteWerte(view: SipConfigView): boolean {
  return Boolean(
    view.authId ||
      view.realm ||
      view.fromDomain ||
      view.port ||
      view.transport ||
      view.registrationExpiration ||
      view.registrationRetryInterval,
  );
}
